import { AxiosResponse } from 'axios'
import { http } from '../../utils'
import { Response, Address } from '../types'
import { withAPIKey } from '../utils'
import { MarketingAttribution } from '../../marketing/attribution'

export type UserLoginRequest = {
  mobile: string
  password: string
}
export type ChangePasswordRequest = {
  newPassword: string
  password: string
  confirmNewPassword?: string
}
export type UserLoginWithFirebaseRequest = {
  mobile: string
  accessToken: string
}

export type Store = {
  name?: string
  storeName?: string
  id?: number
  storeId?: number
  address?: string
  address1?: string
  address2?: string
  storeAddress?: string
  isActive: boolean
  state: string
  createDate: string
  updateDate: string
  gstin?: string
  pan?: string
  userName?: string
  stateCode?: string
  place?: string
  pincode?: number
  actualStateCode?: string
  businessLogo?: string
  signUrl?: string
  signingEnabled?: boolean
  businessAddress?: string
  billingAddress?: Address
  eWayCredentials?: EWayCredentials
  eInvoiceCredentials?: EInvoiceCredentials
  emailCredentials?: EmailCredentials
  gstPortalCredentials?: GSTPortalCredentials
}

export interface EWayCredentials {
  id?: number
  userName: string
  password: string
}

export interface EInvoiceCredentials {
  id?: number
  userName: string
  password: string
}

export interface EmailCredentials {
  id?: number
  email: string
  password: string
}

export interface GSTPortalCredentials {
  id?: number
  gstUsername: string
}

export interface UserLoginData {
  id: number
  store: Store
  config: Config
  isDriveBackUpEnabled: boolean
  name: string
  mobile: string
  email?: string
  webToken: string
  createDate: string
  updateDate: string
  userType: string
  appVersion: string
  deviceId: string
  appVersionNumber: number
  lastUsedAt: string
  lat: number
  lon: number
  activeSubscriptionId: number
  subscriptionEndDate: string
  isOtherStoreFeatureEnabled: boolean
  isMinDaysFeatureEnabled: boolean
  planName: string
  planDiscount: number
  planDiscountIndex: number
  planShownCount: number
  allowZeroRate: boolean
  myRefferalCode: string
  tutorialLink: string
  staffUserId: number
  onboardingDone: boolean | null
  myStores: Store[]
  subscription: Subscription
}
export interface Subscription {
  id: number
  endDate: string
  remainingPoints: number
}

export interface Config {
  id: number
  isMinDaysEnabled: boolean
  isOtherStoreEnabled: boolean
  isGSTEnabled: boolean
  isMixTypeAllowed: boolean
  isRecurringBillsEnabled: boolean
  sgst: number
  cgst: number
  isDriveBackUpEnabled: boolean
  driveFolderName: string
  userId: number
  storeId: number
  updateDate: string
  is30DaysMonthEnabled: boolean
  isCycleBillHelperEnabled?: boolean
}

export type UpdateStore = {
  name: string
  address: string
  businessAddress?: string
  businessAddressDetails?: Address
  gstin?: string
  pan?: string
  hsnCode?: string
  ewayHsnCode?: string
  state?: string
  stateCode?: string
  place?: string
  pincode?: number
  address2?: string
  billingAddress?: Address
  businessLogo?: string
  signUrl?: string
  signingEnabled?: boolean
  eWayCredentials?: EWayCredentials
  eInvoiceCredentials?: EInvoiceCredentials
  emailCredentials?: EmailCredentials
  gstPortalCredentials?: GSTPortalCredentials
}

export type UpdateProfileRequest = {
  name: string
  mobile?: string
  email?: string
  store: UpdateStore
}

export type UpdateSettingsRequest = {
  id: number
  isOtherStoreEnabled: boolean
  isGSTEnabled: boolean
  isRemainingDayDiscountEnabled: boolean
  is3InchPrinter: boolean
  isMinDaysEnabled: boolean
  isMixTypeAllowed: boolean
  billFormat: string
  isRecurringBillsEnabled: boolean
  backupEmail?: string
  is30DaysMonthEnabled: boolean
  isCycleBillHelperEnabled?: boolean
  cgst: number
  sgst: number
  upiId?: string
  enabledChallanBilling?: boolean
  outwardChallanPrefix?: string
  inwardChallanPrefix?: string
  upiId2?: string
  invoiceNoPrefix?: string
  pdfLanguage?: string
}

export interface UserLoginResponse extends Response {
  data: UserLoginData
}

export interface UpdateSettingsAPIResponse extends Response {
  data: Config | null
}

export function createUserLogin(request: UserLoginRequest) {
  return http
    .post<UserLoginRequest>(withAPIKey('/user/password_login'), request)
    .then((response: AxiosResponse) => response)
}

export function updateSettings(request: UpdateSettingsRequest) {
  return http
    .put<UpdateSettingsRequest>(withAPIKey('/user/pvt/config'), request)
    .then((response: AxiosResponse) => response)
}

export function updateUserProfile(
  request: UpdateProfileRequest & { updateSac?: boolean }
) {
  const { updateSac, ...body } = request
  const apiKey = process.env.API_KEY

  // NOTE: `withAPIKey` always appends `?api_key=...`, so we must construct the URL ourselves
  // when we also need query params like `update_sac=true`.
  const url =
    updateSac === true
      ? `/user/pvt?update_sac=true&api_key=${apiKey}`
      : `/user/pvt?api_key=${apiKey}`

  return http.put(url, body).then((response: AxiosResponse) => response)
}

export function createUserFirebaseLogin(request: UserLoginWithFirebaseRequest) {
  const { mobile, accessToken } = request
  return http
    .get<UserLoginRequest>(withAPIKey(`/user/login/${mobile}/${accessToken}`))
    .then((response: AxiosResponse) => response)
}

export function logout() {
  return http
    .get('/user/pvt/logout')
    .then((response: AxiosResponse) => response)
}

export function createGetUserProfileData() {
  return http
    .get(`/user/pvt/profile`)
    .then((response: AxiosResponse) => response)
}

export function createChangePassword(request: ChangePasswordRequest) {
  return http
    .post<ChangePasswordRequest>(
      withAPIKey('/user/pvt/change_password'),
      request
    )
    .then((response: AxiosResponse) => response)
}

export type SendOTPDataObject = {
  verificationId: string
  phoneNumber: string
  expirationInSeconds: number
  status: string
}
export interface SendOTPResponse extends Response {
  data: SendOTPDataObject
}
export function createSendOTP(identifier: string) {
  return http
    .get(`/user/send_otp/${encodeURIComponent(identifier)}`)
    .then((response: AxiosResponse) => response)
}

export function createSendLinkOTP(identifier: string) {
  return http
    .get(`/user/pvt/send_link_otp/${encodeURIComponent(identifier)}`)
    .then((response: AxiosResponse) => response)
}

export type VerifyOtpRequest = {
  verificationId: string
  otp: string
  deviceId: string
  deviceName: string
  marketingAttribution?: MarketingAttribution
}

export function createVerifyOtp(request: VerifyOtpRequest) {
  return http
    .post<VerifyOtpRequest>(withAPIKey('/user/verify_otp'), request)
    .then((response: AxiosResponse) => response)
}

export function createVerifyLinkOtp(request: VerifyOtpRequest) {
  return http
    .post<VerifyOtpRequest>('/user/pvt/verify_link_otp', request)
    .then((response: AxiosResponse) => response)
}

export interface BucketObject {
  id?: string
  sourceFilePath?: string
  downloadUrl?: string
  uploadUrl?: string
  fileName?: string
  contentType?: string
  mimeType?: string
}

export interface ObjectStorageAPIEndPoint {
  getPresignedUrl(): Promise<AxiosResponse<Response>>
}

export function getPresignedUrl() {
  return http
    .get('/object_storage/pvt/presigned_url')
    .then((response: AxiosResponse) => response)
}

export function uploadObject(
  filePath: string,
  file: File
): Promise<BucketObject> {
  return new Promise(async (resolve, reject) => {
    try {
      // Get presigned URL from backend (secure)
      const response = await getPresignedUrl()
      const bucketObject = response.data.data

      if (!bucketObject) {
        reject(new Error('Failed to get presigned URL'))
        return
      }

      console.log('Presigned URL from backend:', bucketObject.uploadUrl)
      console.log('File type:', file.type)
      console.log('File size:', file.size)

      // Use XMLHttpRequest for minimal headers
      const xhr = new XMLHttpRequest()

      xhr.open('PUT', bucketObject.uploadUrl, true)

      // Use the mimeType from backend for Content-Type
      if (bucketObject.mimeType) {
        xhr.setRequestHeader('Content-Type', bucketObject.mimeType)
      }

      console.log('Sending Content-Type:', bucketObject.mimeType)

      xhr.onload = function () {
        console.log('XHR Response Status:', xhr.status)

        if (xhr.status === 200) {
          console.log('Upload successful')
          resolve(bucketObject)
        } else {
          console.error('Upload error:', xhr.responseText)
          reject(
            new Error(`Upload failed: ${xhr.status} - ${xhr.responseText}`)
          )
        }
      }

      xhr.onerror = function () {
        console.error('XHR Network error')
        reject(new Error('Network error during upload'))
      }

      xhr.send(file)
    } catch (error) {
      console.error('Upload error:', error)
      reject(error)
    }
  })
}

export function uploadBusinessLogo(
  file: File
): Promise<{ downloadUrl: string }> {
  const formData = new FormData()
  formData.append('businessLogo', file)

  return http
    .post('/user/pvt/upload-business-logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then((response: AxiosResponse) => response.data)
}

export interface UsersCountResponse extends Response {
  data: number
}

export function getUsersCount() {
  return http
    .get('/user/users_count')
    .then((response: AxiosResponse) => response.data)
}
